import { create } from 'zustand'

/** An Export menu on screen that can be opened when one of its exports is ready. */
export interface ExportMenuEntry {
  /** Opens the menu without taking focus. */
  open: () => void
  /** Its button, to tell whether it's on screen. */
  element: () => HTMLElement | null
}

/** A grid's Export menu that can print its PDF, for Ctrl+P. */
export interface ExportPrinter {
  /** Prints as the menu's print button does. */
  print: () => void
  /** Its button, to tell whether it's on screen and which grid is nearest. */
  element: () => HTMLElement | null
}

interface ExportMenusState {
  /** By exportScopeKey: a table's or list's Export menu, or `all` for the header's Exports. */
  menus: Record<string, ExportMenuEntry>
  /** Adds a menu; returns how to take it out again. */
  register: (scopeKey: string, entry: ExportMenuEntry) => () => void
  /** Every grid's Export menu that prints (two grids of one table are two). */
  printers: ExportPrinter[]
  /** Adds a printer; returns how to take it out again. */
  registerPrinter: (printer: ExportPrinter) => () => void
}

/**
 * The Export menus mounted now, for Open When Ready and Ctrl+P
 * (docs/specs/EXPORTS.md).
 */
export const useExportMenusStore = create<ExportMenusState>()((set, get) => ({
  menus: {},
  printers: [],
  registerPrinter: (printer) => {
    set((state) => ({ printers: [...state.printers, printer] }))
    return () => set((state) => ({ printers: state.printers.filter((other) => other !== printer) }))
  },
  register: (scopeKey, entry) => {
    set((state) => ({ menus: { ...state.menus, [scopeKey]: entry } }))
    return () => {
      if (get().menus[scopeKey] !== entry) return
      set((state) => {
        const menus = { ...state.menus }
        delete menus[scopeKey]
        return { menus }
      })
    }
  },
}))
